"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { isRestoringDocsScroll } from "./use-scroll-restoration";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

export function DocsArticle({ html }: { html: string }) {
  const [image, setImage] = useState<{ src: string; alt: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState({ message: "", error: false });
  const copyTimers = useRef(
    new Map<HTMLElement, ReturnType<typeof setTimeout>>(),
  );
  useEffect(
    () => () => {
      copyTimers.current.forEach(clearTimeout);
      copyTimers.current.clear();
    },
    [html],
  );
  useEffect(() => {
    // The browser's initial fragment scroll can run before the streamed article
    // and its nested scroll container are ready. Resolve it again after mount.
    const frame = requestAnimationFrame(() => {
      if (isRestoringDocsScroll() || !window.location.hash) return;
      let id: string;
      try {
        id = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      const container = document.getElementById("docs-scroll-container");
      const target = document.getElementById(id);
      if (!container || !target || !container.contains(target)) return;
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      container.scrollTo({
        top:
          container.scrollTop +
          target.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          container.clientTop -
          margin,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [html]);

  const trigger = useRef<HTMLImageElement | null>(null);
  const openImage = useCallback((target: EventTarget) => {
    if (
      !(target instanceof HTMLImageElement) ||
      target.classList.contains("emoji")
    )
      return false;
    trigger.current = target;
    setImage({ src: target.currentSrc || target.src, alt: target.alt });
    setOpen(true);
    return true;
  }, []);
  const article = useMemo(
    () => (
      <article
        className="docs-article"
        onClick={(event) => {
          const copyButton =
            event.target instanceof Element
              ? event.target.closest<HTMLButtonElement>(".docs-code-copy")
              : null;
          if (copyButton) {
            const code = copyButton.parentElement?.querySelector("code");
            if (!code) return;
            const copy = code.cloneNode(true) as HTMLElement;
            copy
              .querySelectorAll<HTMLImageElement>("img.emoji")
              .forEach((emoji) => emoji.replaceWith(emoji.alt));
            void (
              navigator.clipboard
                ? navigator.clipboard.writeText(copy.textContent || "")
                : Promise.reject(new Error("Clipboard unavailable"))
            ).then(
              () => {
                clearTimeout(copyTimers.current.get(copyButton));
                copyButton.dataset.copied = "true";
                copyButton.textContent = "Copied";
                copyButton.setAttribute("aria-label", "Code copied");
                setCopyStatus({ message: "Code copied.", error: false });
                copyTimers.current.set(
                  copyButton,
                  setTimeout(() => {
                    delete copyButton.dataset.copied;
                    copyButton.textContent = "Copy";
                    copyButton.setAttribute("aria-label", "Copy code");
                    copyTimers.current.delete(copyButton);
                  }, 1800),
                );
              },
              () =>
                setCopyStatus({
                  message:
                    "Unable to copy. Select the code and copy it manually.",
                  error: true,
                }),
            );
            return;
          }
          if (openImage(event.target)) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (
            (event.key === "Enter" || event.key === " ") &&
            openImage(event.target)
          )
            event.preventDefault();
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    ),
    [html, openImage],
  );
  return (
    <>
      {article}
      <p
        role="status"
        className={copyStatus.error ? "mt-2 text-sm text-red-500" : "sr-only"}
      >
        {copyStatus.message}
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          overlayClassName="docs-image-overlay"
          className="docs-image-dialog w-fit max-w-[95vw] gap-0 border-0 bg-transparent p-0 shadow-none sm:max-w-[95vw]"
          aria-describedby={undefined}
          showCloseButton={false}
          onCloseAutoFocus={() =>
            trigger.current?.focus({ preventScroll: true })
          }
        >
          <DialogTitle className="sr-only">
            {image?.alt || "Expanded documentation image"}
          </DialogTitle>
          <DialogClose
            className="absolute -top-10 right-0 rounded-full bg-sidebar p-1.5 text-primary transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close image"
          >
            <X className="size-5" />
          </DialogClose>
          {image && (
            // Preserve the source dimensions for documentation screenshots.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={image.src}
              alt={image.alt}
              className="block h-auto max-h-[calc(100svh-7rem)] w-auto max-w-[95vw] rounded-xl"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
